import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import EmptyState from '@/components/EmptyState';
import DialogFormFooter from '@/components/DialogFormFooter';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Mail, FileText } from 'lucide-react';


const Submissions = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [formTemplates, setFormTemplates] = useState([]);
  
  // Dialogs
  const [showNotifyDialog, setShowNotifyDialog] = useState(false);
  const [showLinkFormDialog, setShowLinkFormDialog] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  
  // Form states
  const [notificationData, setNotificationData] = useState({ subject: '', message: '' });
  const [linkedFormId, setLinkedFormId] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');

  const fetchSubmissions = useCallback(async () => {
    try {
      const response = await api.get(`/form-submissions`);
      setSubmissions(response.data);
      setFilteredSubmissions(response.data);
    } catch (error) {
      console.error('Failed to fetch submissions', error);
      toast.error('Failed to load submissions');
    }
  }, []);

  const fetchFormTemplates = useCallback(async () => {
    try {
      const response = await api.get(`/form-templates`);
      setFormTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch form templates', error);
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (user) {
      if (user.role !== 'admin') {
        toast.error('Access denied. Admin privileges required.');
        navigate('/');
        return;
      }
      fetchSubmissions();
      fetchFormTemplates();
    }
  }, [user, loading, navigate, fetchSubmissions, fetchFormTemplates]);

  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredSubmissions(submissions);
    } else {
      setFilteredSubmissions(submissions.filter(s => s.status === statusFilter));
    }
  }, [statusFilter, submissions]);

  const updateStatus = async (submissionId, newStatus) => {
    try {
      await api.patch(
        `/form-submissions/${submissionId}`,
        { status: newStatus }
      );
      toast.success('Status updated successfully');
      fetchSubmissions();
    } catch (error) {
      console.error('Failed to update status', error);
      toast.error('Failed to update status');
    }
  };

  const sendNotification = async () => {
    try {
      const clientEmail = selectedSubmission.data.email;
      await api.post(
        `/notifications/send`,
        {
          recipient_email: clientEmail,
          subject: notificationData.subject,
          message: notificationData.message
        }
      );
      toast.success('Notification sent successfully');
      setShowNotifyDialog(false);
      setNotificationData({ subject: '', message: '' });
    } catch (error) {
      console.error('Failed to send notification', error);
      toast.error('Failed to send notification');
    }
  };

  const linkForm = async () => {
    try {
      await api.patch(
        `/form-submissions/${selectedSubmission.id}`,
        { linked_form_id: linkedFormId }
      );
      
      // Send email to client
      const clientEmail = selectedSubmission.data.email;
      const formName = formTemplates.find(f => f.id === linkedFormId)?.name || 'Additional Information';
      await api.post(
        `/notifications/send`,
        {
          recipient_email: clientEmail,
          subject: 'Additional Information Required',
          message: `<p>We need some additional information to process your ${selectedSubmission.form_name} submission.</p><p>Please fill out the following form: <strong>${formName}</strong></p><p>Our team will contact you with the link shortly.</p>`
        }
      );
      
      toast.success('Form linked and notification sent');
      setShowLinkFormDialog(false);
      setLinkedFormId('');
      fetchSubmissions();
    } catch (error) {
      console.error('Failed to link form', error);
      toast.error('Failed to link form');
    }
  };

  const saveNotes = async () => {
    try {
      await api.patch(
        `/form-submissions/${selectedSubmission.id}`,
        { review_notes: reviewNotes }
      );
      toast.success('Notes saved successfully');
      setShowNotesDialog(false);
      setReviewNotes('');
      fetchSubmissions();
    } catch (error) {
      console.error('Failed to save notes', error);
      toast.error('Failed to save notes');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500';
      case 'reviewed': return 'bg-blue-500';
      case 'approved': return 'bg-green-500';
      case 'rejected': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <PageShell>
      <PageHeader
        title="Form Submissions"
        titleTestId="submissions-heading"
        actions={
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="reviewed">Reviewed</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        }
      />

        {filteredSubmissions.length > 0 ? (
          <div className="space-y-4">
            {filteredSubmissions.map((submission, index) => (
              <Card key={submission.id} data-testid={`submission-card-${index}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="font-serif text-xl font-semibold text-primary mb-2">
                        {submission.form_name}
                      </CardTitle>
                      <p className="font-sans text-sm text-muted-foreground">
                        Submitted: {new Date(submission.submitted_at).toLocaleString()}
                      </p>
                      {submission.additional_info_requested && (
                        <p className="font-sans text-sm text-accent mt-1">
                          ⚠️ Additional info requested
                        </p>
                      )}
                    </div>
                    <Badge className={`${getStatusColor(submission.status)} text-white`}>
                      {submission.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {Object.entries(submission.data).map(([key, value]) => (
                      <div key={key}>
                        <p className="font-sans text-sm font-medium text-primary capitalize">
                          {key.replace(/_/g, ' ')}
                        </p>
                        <p className="font-sans text-sm text-muted-foreground">{value || 'N/A'}</p>
                      </div>
                    ))}
                  </div>
                  
                  {submission.review_notes && (
                    <div className="mb-4 p-3 bg-secondary border border-border">
                      <p className="font-sans text-sm font-medium text-primary mb-1">Review Notes:</p>
                      <p className="font-sans text-sm text-muted-foreground">{submission.review_notes}</p>
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => updateStatus(submission.id, 'reviewed')}
                      variant="outline"
                      size="sm"
                      data-testid={`review-button-${index}`}
                    >
                      Mark as Reviewed
                    </Button>
                    <Button
                      onClick={() => updateStatus(submission.id, 'approved')}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={`approve-button-${index}`}
                    >
                      Approve
                    </Button>
                    <Button
                      onClick={() => updateStatus(submission.id, 'rejected')}
                      size="sm"
                      className="bg-red-600 hover:bg-red-700"
                      data-testid={`reject-button-${index}`}
                    >
                      Reject
                    </Button>
                    <Button
                      onClick={() => {
                        setSelectedSubmission(submission);
                        setShowNotifyDialog(true);
                      }}
                      size="sm"
                      variant="outline"
                      data-testid={`notify-button-${index}`}
                    >
                      <Mail size={16} className="mr-1" /> Send Email
                    </Button>
                    <Button
                      onClick={() => {
                        setSelectedSubmission(submission);
                        setShowLinkFormDialog(true);
                      }}
                      size="sm"
                      variant="outline"
                      data-testid={`link-form-button-${index}`}
                    >
                      <FileText size={16} className="mr-1" /> Request More Info
                    </Button>
                    <Button
                      onClick={() => {
                        setSelectedSubmission(submission);
                        setReviewNotes(submission.review_notes || '');
                        setShowNotesDialog(true);
                      }}
                      size="sm"
                      variant="outline"
                      data-testid={`notes-button-${index}`}
                    >
                      Add Notes
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <EmptyState icon={FileText} message="No submissions found" />
        )}

      {/* Send Notification Dialog */}
      <Dialog open={showNotifyDialog} onOpenChange={setShowNotifyDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-bold text-primary">
              Send Email Notification
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Recipient</Label>
              <Input
                value={selectedSubmission?.data?.email || ''}
                disabled
                className="bg-secondary"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Subject</Label>
              <Input
                value={notificationData.subject}
                onChange={(e) => setNotificationData({ ...notificationData, subject: e.target.value })}
                placeholder="Email subject"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Message</Label>
              <Textarea
                value={notificationData.message}
                onChange={(e) => setNotificationData({ ...notificationData, message: e.target.value })}
                placeholder="Email message"
                rows={6}
              />
            </div>
            <DialogFormFooter
              onCancel={() => setShowNotifyDialog(false)}
              onSubmit={sendNotification}
              submitLabel="Send Email"
              disabled={!notificationData.subject || !notificationData.message}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Link Form Dialog */}
      <Dialog open={showLinkFormDialog} onOpenChange={setShowLinkFormDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-bold text-primary">
              Request Additional Information
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="font-sans text-sm text-muted-foreground">
              Select a form to request additional information from the client. They will receive an email notification.
            </p>
            <div>
              <Label className="text-sm font-medium mb-2 block">Select Form</Label>
              <Select value={linkedFormId} onValueChange={setLinkedFormId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a form" />
                </SelectTrigger>
                <SelectContent>
                  {formTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFormFooter
              onCancel={() => setShowLinkFormDialog(false)}
              onSubmit={linkForm}
              submitLabel="Send Request"
              disabled={!linkedFormId}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Notes Dialog */}
      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-bold text-primary">
              Add Review Notes
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Notes</Label>
              <Textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Add your review notes here..."
                rows={6}
              />
            </div>
            <DialogFormFooter
              onCancel={() => setShowNotesDialog(false)}
              onSubmit={saveNotes}
              submitLabel="Save Notes"
            />
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};

export default Submissions;
